/**
 * Google reports each U.S. territory as its own country, with an island,
 * district, or municipio (or nothing) as administrative_area_level_1, so a
 * territory's code comes from the country component.
 */
export const US_TERRITORY_CODES: ReadonlySet<string> = new Set(['PR', 'GU', 'AS', 'VI', 'MP']);

/**
 * Territories where Google often has no house numbers, so the address picker
 * also offers villages, which carry no ZIP. Mirrors the backend's
 * `UNNUMBERED_TERRITORIES`.
 */
export const UNNUMBERED_TERRITORY_CODES: ReadonlySet<string> = new Set(['GU', 'VI', 'MP', 'AS']);

interface AddressComponentLike {
  types?: string[];
  shortText?: string;
  shortName?: string;
  short_name?: string;
  longText?: string;
  longName?: string;
  long_name?: string;
}

const shortValue = (c: AddressComponentLike | undefined): string | undefined =>
  c?.shortText || c?.shortName || c?.short_name || undefined;

/** The state or territory code ("CA", "PR") for Google address components. */
export const stateCodeFromComponents = (components: unknown): string | undefined => {
  if (!Array.isArray(components)) return undefined;
  const find = (type: string) =>
    (components as AddressComponentLike[]).find(
      (c) => Array.isArray(c?.types) && c.types.includes(type)
    );
  const country = shortValue(find('country'));
  if (country && US_TERRITORY_CODES.has(country)) return country;
  return shortValue(find('administrative_area_level_1'));
};

const longValue = (c: AddressComponentLike | undefined): string | undefined =>
  c?.longText || c?.longName || c?.long_name || undefined;

/** True for a U.S. territory code ("PR", "GU", ...). */
export const isTerritoryCode = (code: string | null | undefined): boolean =>
  Boolean(code && US_TERRITORY_CODES.has(code.toUpperCase()));

/**
 * The area named before the state in a location label: the county, else the
 * city. Matches the backend's ballot-record label ("Travis County, TX",
 * "Tamuning, GU"); territories and some cities have no county component.
 */
export const areaFromComponents = (components: unknown): string | undefined => {
  if (!Array.isArray(components)) return undefined;
  const find = (type: string) =>
    (components as AddressComponentLike[]).find(
      (c) => Array.isArray(c?.types) && c.types.includes(type)
    );
  return longValue(find('administrative_area_level_2')) || longValue(find('locality'));
};
